// T00M1EHFWBNJ — the coaching's time zone and the viewer's.
//
// Appointments are wall-clock times in the coaching's zone (calendar settings,
// Europe/Berlin by default). They are SHOWN in the viewer's local time, so
// whoever travels sees when to join, and ENTERED in the coaching's zone, so a
// coach abroad cannot shift a session by accident.

export const DEFAULT_COACHING_TIME_ZONE = 'Europe/Berlin';

let coachingZone = DEFAULT_COACHING_TIME_ZONE;

/** Set from the user data once they are loaded. Unknown zones keep the default. */
export function setCoachingTimeZone(zone: string | null | undefined): void {
  coachingZone = zone && isTimeZone(zone) ? zone : DEFAULT_COACHING_TIME_ZONE;
}

export function coachingTimeZone(): string {
  return coachingZone;
}

/** The browser's zone, e.g. `America/New_York`. */
export function deviceTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_COACHING_TIME_ZONE;
}

/** Whether the viewer is somewhere else than the coaching — then times are labelled. */
export function timeZoneDiffers(device: string = deviceTimeZone(), coaching: string = coachingTimeZone()): boolean {
  return device !== coaching;
}

export function isTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', {timeZone: zone});
    return true;
  } catch {
    return false;
  }
}

/** All IANA zones the browser knows, for the setting. */
export function timeZoneOptions(): string[] {
  const supported = (Intl as unknown as {supportedValuesOf?: (key: string) => string[]}).supportedValuesOf?.('timeZone') ?? [];
  return supported.includes(DEFAULT_COACHING_TIME_ZONE) ? supported : [DEFAULT_COACHING_TIME_ZONE, ...supported];
}

/** `America/New_York` → `New York`, `Europe/Berlin` → `Berlin`. */
export function timeZoneCity(zone: string): string {
  return (zone.split('/').pop() ?? zone).replaceAll('_', ' ');
}

/**
 * The current offset, e.g. `UTC+1`, `UTC-4`, `UTC+5:30` or `UTC±0`. Computed
 * from the wall clock instead of Intl's `shortOffset` name: that string differs
 * between ICU versions (`GMT` on one Node, `GMT+0` on another).
 */
export function timeZoneOffset(zone: string, at: Date = new Date()): string {
  const minutes = Math.round(offsetMs(at.getTime(), zone) / 60_000);
  if (minutes === 0) {
    return 'UTC±0';
  }
  const hours = Math.floor(Math.abs(minutes) / 60);
  const rest = Math.abs(minutes) % 60;
  return `UTC${minutes < 0 ? '-' : '+'}${hours}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`;
}

/** `Berlin (UTC+1)` — for the setting and the hints. */
export function timeZoneLabel(zone: string, at: Date = new Date()): string {
  return `${timeZoneCity(zone)} (${timeZoneOffset(zone, at)})`;
}

/** Wall clock of an instant in a zone: date `YYYY-MM-DD` and time `HH:mm`. */
export function zoneParts(instant: Date | string, zone: string): {date: string; time: string} {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(instant)).map((part) => [part.type, part.value]));
  return {date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}`};
}

/** Milliseconds the zone is ahead of UTC at that instant. */
function offsetMs(instant: number, zone: string): number {
  const {date, time} = zoneParts(new Date(instant), zone);
  const asUtc = Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)), Number(time.slice(0, 2)), Number(time.slice(3, 5)));
  return asUtc - Math.floor(instant / 60_000) * 60_000;
}

/**
 * The instant of a wall-clock time in a zone: `2026-10-29`, `18:00` in
 * Europe/Berlin is 17:00 UTC (winter), the same in October is 16:00 UTC.
 */
export function wallTimeToInstant(date: string, time: string, zone: string): Date {
  const guess = Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)), Number(time.slice(0, 2)), Number(time.slice(3, 5)));
  const first = guess - offsetMs(guess, zone);
  // Around a switch the offset at the guess can differ from the one at the result.
  return new Date(guess - offsetMs(first, zone));
}
